/**
 * URL namespaces for structured search so each surface can keep independent state
 * in the same address bar (table vs sidebar vs prompt analytics, requests vs traces).
 */

export type StructuredSearchUrlNamespaceId =
  | "requestsTable"
  | "requestsSidebar"
  | "tracesTable"
  | "tracesSidebar"
  | "promptAnalytics"
  | "workflowAnalytics";

export interface StructuredSearchUrlParamKeys {
  filters: string;
  q: string;
  page: string;
  pageSize: string;
  sortBy: string;
  sortDesc: string;
  startTime: string;
  endTime: string;
}

const NAMESPACE_PARAM_FIELDS: (keyof StructuredSearchUrlParamKeys)[] = [
  "filters",
  "q",
  "page",
  "pageSize",
  "sortBy",
  "sortDesc",
  "startTime",
  "endTime",
];

export interface StructuredSearchUrlFallback extends Partial<StructuredSearchUrlParamKeys> {
  migrate?: boolean;
}

export interface StructuredSearchUrlNamespace extends StructuredSearchUrlParamKeys {
  id: StructuredSearchUrlNamespaceId;
  /**
   * Older param names still accepted on read (migration). Writes always use the
   * primary keys above.
   */
  readFallbacks?: StructuredSearchUrlFallback[];
}

const inheritFrom = (namespace: StructuredSearchUrlNamespace): StructuredSearchUrlFallback => ({
  filters: namespace.filters,
  q: namespace.q,
  page: namespace.page,
  pageSize: namespace.pageSize,
  sortBy: namespace.sortBy,
  sortDesc: namespace.sortDesc,
  startTime: namespace.startTime,
  endTime: namespace.endTime,
});

export const REQUESTS_TABLE_URL_NAMESPACE: StructuredSearchUrlNamespace = {
  id: "requestsTable",
  filters: "requestsStructuredFilters",
  q: "requestsQ",
  page: "requestsPage",
  pageSize: "requestsPageSize",
  sortBy: "requestsSortBy",
  sortDesc: "requestsSortDesc",
  startTime: "requests_start_time",
  endTime: "requests_end_time",
  readFallbacks: [
    {
      migrate: true,
      filters: "structuredFilters",
      q: "q",
      page: "page",
      pageSize: "page_size",
      sortBy: "sortBy",
      sortDesc: "sortDesc",
    },
    {
      startTime: "startTime",
      endTime: "endTime",
    },
  ],
};

export const REQUESTS_SIDEBAR_URL_NAMESPACE: StructuredSearchUrlNamespace = {
  id: "requestsSidebar",
  filters: "sidebarStructuredFilters",
  q: "sidebarQ",
  page: "sidebarPage",
  pageSize: "sidebarPageSize",
  sortBy: "sidebarSortBy",
  sortDesc: "sidebarSortDesc",
  startTime: "sidebar_requests_start_time",
  endTime: "sidebar_requests_end_time",
  readFallbacks: [
    inheritFrom(REQUESTS_TABLE_URL_NAMESPACE),
    // Request-detail URLs historically used the global requests-table keys for the open sidebar.
    {
      filters: "structuredFilters",
      q: "q",
      page: "page",
      pageSize: "page_size",
      sortBy: "sortBy",
      sortDesc: "sortDesc",
    },
    {
      startTime: "startTime",
      endTime: "endTime",
    },
  ],
};

export const TRACES_TABLE_URL_NAMESPACE: StructuredSearchUrlNamespace = {
  id: "tracesTable",
  filters: "tracesStructuredFilters",
  q: "tracesQ",
  page: "tracesPage",
  pageSize: "tracesPageSize",
  sortBy: "tracesSortBy",
  sortDesc: "tracesSortDesc",
  startTime: "traces_start_time",
  endTime: "traces_end_time",
};

export const TRACES_SIDEBAR_URL_NAMESPACE: StructuredSearchUrlNamespace = {
  id: "tracesSidebar",
  filters: "sidebarTracesStructuredFilters",
  q: "sidebarTracesQ",
  page: "sidebarTracesPage",
  pageSize: "sidebarTracesPageSize",
  sortBy: "sidebarTracesSortBy",
  sortDesc: "sidebarTracesSortDesc",
  startTime: "sidebar_traces_start_time",
  endTime: "sidebar_traces_end_time",
  readFallbacks: [inheritFrom(TRACES_TABLE_URL_NAMESPACE)],
};

export const PROMPT_ANALYTICS_URL_NAMESPACE: StructuredSearchUrlNamespace = {
  id: "promptAnalytics",
  filters: "promptStructuredFilters",
  q: "promptQ",
  page: "promptPage",
  pageSize: "promptPageSize",
  sortBy: "promptSortBy",
  sortDesc: "promptSortDesc",
  startTime: "prompt_requests_start_time",
  endTime: "prompt_requests_end_time",
  // No readFallbacks: legacy globals (structuredFilters, requests_*, page_size) belong to
  // table/sidebar only and must never hydrate prompt Analytics.
};

export const WORKFLOW_ANALYTICS_URL_NAMESPACE: StructuredSearchUrlNamespace = {
  id: "workflowAnalytics",
  filters: "workflowStructuredFilters",
  q: "workflowQ",
  page: "workflowPage",
  pageSize: "workflowPageSize",
  sortBy: "workflowSortBy",
  sortDesc: "workflowSortDesc",
  startTime: "workflow_traces_start_time",
  endTime: "workflow_traces_end_time",
  // No readFallbacks: traces table/sidebar namespaces must never hydrate workflow Analytics.
};

export const STRUCTURED_SEARCH_URL_NAMESPACES: Record<
  StructuredSearchUrlNamespaceId,
  StructuredSearchUrlNamespace
> = {
  requestsTable: REQUESTS_TABLE_URL_NAMESPACE,
  requestsSidebar: REQUESTS_SIDEBAR_URL_NAMESPACE,
  tracesTable: TRACES_TABLE_URL_NAMESPACE,
  tracesSidebar: TRACES_SIDEBAR_URL_NAMESPACE,
  promptAnalytics: PROMPT_ANALYTICS_URL_NAMESPACE,
  workflowAnalytics: WORKFLOW_ANALYTICS_URL_NAMESPACE,
};

export const getStructuredSearchUrlNamespace = (
  id: StructuredSearchUrlNamespaceId,
): StructuredSearchUrlNamespace => STRUCTURED_SEARCH_URL_NAMESPACES[id];

/** Namespaces that historically shared request-log URL params (including pre-structured filters). */
export const supportsLegacyRequestFilterParams = (
  namespace: StructuredSearchUrlNamespace | StructuredSearchUrlNamespaceId,
): boolean => {
  const id = typeof namespace === "string" ? namespace : namespace.id;
  return id === "requestsTable" || id === "requestsSidebar";
};

const namespaceParamKeyList = (namespace: StructuredSearchUrlNamespace): string[] =>
  NAMESPACE_PARAM_FIELDS.map(field => namespace[field]);

const fallbackParamKeyList = (namespace: StructuredSearchUrlNamespace): string[] =>
  (namespace.readFallbacks ?? []).flatMap(fallback =>
    NAMESPACE_PARAM_FIELDS.map(field => fallback[field]).filter((key): key is string => !!key),
  );

/** Every structured-search URL key across namespaces (for keepRelevantSearchParams). */
export const ALL_STRUCTURED_SEARCH_URL_PARAM_KEYS: string[] = Array.from(
  new Set(
    Object.values(STRUCTURED_SEARCH_URL_NAMESPACES).flatMap(namespace => [
      ...namespaceParamKeyList(namespace),
      ...fallbackParamKeyList(namespace),
    ]),
  ),
);

export const clearStructuredSearchUrlNamespace = (
  params: URLSearchParams,
  namespace: StructuredSearchUrlNamespace,
): void => {
  for (const key of namespaceParamKeyList(namespace)) {
    params.delete(key);
  }
};

const migratableFallbacks = (
  namespace: StructuredSearchUrlNamespace,
): StructuredSearchUrlFallback[] =>
  (namespace.readFallbacks ?? []).filter(({ migrate }) => migrate);

export const hasParamsToMigrate = (
  searchParams: URLSearchParams,
  namespace: StructuredSearchUrlNamespace,
): boolean =>
  migratableFallbacks(namespace).some(fallback =>
    NAMESPACE_PARAM_FIELDS.some(field => {
      const key = fallback[field];
      return !!key && searchParams.has(key);
    }),
  );

export const deleteMigratedParams = (
  params: URLSearchParams,
  namespace: StructuredSearchUrlNamespace,
): void => {
  for (const fallback of migratableFallbacks(namespace)) {
    for (const field of NAMESPACE_PARAM_FIELDS) {
      const key = fallback[field];
      if (key) {
        params.delete(key);
      }
    }
  }
};

const readParamWithFallbacks = (
  searchParams: URLSearchParams,
  primaryKey: string,
  fallbackKeys: (string | undefined)[],
): string | null => {
  if (searchParams.has(primaryKey)) {
    return searchParams.get(primaryKey);
  }
  for (const key of fallbackKeys) {
    if (key && searchParams.has(key)) {
      return searchParams.get(key);
    }
  }
  return null;
};

const fallbackKeysFor = (
  namespace: StructuredSearchUrlNamespace,
  field: keyof StructuredSearchUrlParamKeys,
): (string | undefined)[] => namespace.readFallbacks?.map(fallback => fallback[field]) ?? [];

export const readNamespacedParam = (
  searchParams: URLSearchParams,
  namespace: StructuredSearchUrlNamespace,
  field: keyof StructuredSearchUrlParamKeys,
): string | null =>
  readParamWithFallbacks(searchParams, namespace[field], fallbackKeysFor(namespace, field));

export const hasNamespacedParam = (
  searchParams: URLSearchParams,
  namespace: StructuredSearchUrlNamespace,
  field: keyof StructuredSearchUrlParamKeys,
): boolean => {
  if (searchParams.has(namespace[field])) {
    return true;
  }
  return fallbackKeysFor(namespace, field).some(key => !!key && searchParams.has(key));
};
